// Occupants: objects sitting on another object (workers on a space, on a card or on an event),
// found through a brick's `occupantsFrom` selector by their `stack` or `at` reference, and drawn
// as seat-colored pips with the host's remaining capacity.

import type { BrickInstance } from '../presentation/types';
import type { ObjectView } from '../model/table';
import { useScene, zonesOf } from '../scene/SceneContext';
import { Glyph } from '../presentation/Glyph';

/** Occupants of each host object, by host ID. Hosts with none are absent. */
export function useOccupants(inst: BrickInstance, seat: string | null): (hostId: string) => ObjectView[] {
  const { table } = useScene();
  const from = inst.props?.occupantsFrom as string | undefined;
  if (!from) return () => [];
  const byHost = new Map<string, ObjectView[]>();
  for (const z of zonesOf(from, table, seat)) for (const o of z.objects) {
    const host = o.stack ?? o.at;
    if (host) byHost.set(host, [...(byHost.get(host) ?? []), o]);
  }
  return host => byHost.get(host) ?? [];
}

/**
 * A field by path: `fields.x`, `catalog.fields.x`, `catalog.key`, `catalog.prefix` (the catalog key up
 * to its first colon, e.g. `forest` for `forest:two-any`), or a bare name (object field, then catalog field).
 */
export function fieldOf(o: ObjectView, path: string): unknown {
  if (path === 'catalog.key') return o.catalogKey ?? undefined;
  if (path === 'catalog.prefix') return o.catalogKey ? o.catalogKey.split(':')[0] : undefined;
  if (path.startsWith('catalog.fields.')) return o.catalog?.fields?.[path.slice(15)];
  const name = path.startsWith('fields.') ? path.slice(7) : path;
  return o.fields[name] ?? o.catalog?.fields?.[name];
}

interface PipsProps {
  occupants: ObjectView[];
  /** Occupants known only by number (no occupant objects), drawn as neutral pips. */
  count?: number;
  /** Numeric capacity draws that many slots; null means shared (one open slot after the occupants). */
  capacity: number | null;
}

/** Decorative pips (the host's label already names its occupants and capacity for assistive tech). */
export function Pips({ occupants, count = occupants.length, capacity }: PipsProps) {
  const { seatColor } = useScene();
  const anonymous = Math.max(0, count - occupants.length);
  const open = capacity === null ? 1 : Math.max(0, capacity - count);
  if (occupants.length + anonymous + open === 0) return null;
  return (
    <span className="pips" aria-hidden="true">
      {occupants.map(o => <span key={o.id} className="pip is-taken" style={{ '--seat-color': seatColor(o.owner) } as React.CSSProperties}><Glyph name="gi:meeple" size={14} /></span>)}
      {Array.from({ length: anonymous }, (_, i) => <span key={`a${i}`} className="pip is-taken is-anonymous"><Glyph name="gi:meeple" size={14} /></span>)}
      {Array.from({ length: Math.min(open, 6) }, (_, i) => <span key={`o${i}`} className={`pip is-open${capacity === null ? ' is-shared' : ''}`} />)}
    </span>
  );
}
